/**
 * The settings sections, in nav order: the desktop side nav and the handheld
 * section list (ADR 0048 §9.3) are both drawn from this one table.
 */

export interface SettingsSectionLink {
  /** Stable id. */
  id: string;
  to: string;
  label: string;
  description: string;
  /** NavLink `end` — only the index link needs exact matching. */
  end?: boolean;
}

export const SETTINGS_SECTIONS: readonly SettingsSectionLink[] = [
  {
    id: 'general',
    to: '/settings',
    label: 'General',
    description: 'Appearance, date and time, reading, signature and video calls',
    end: true,
  },
  { id: 'accounts', to: '/settings/accounts', label: 'Accounts', description: 'Connected mailboxes and quota' },
  { id: 'filters', to: '/settings/filters', label: 'Filters', description: 'Sieve rules and raw scripts' },
  {
    id: 'vacation',
    to: '/settings/out-of-office',
    label: 'Out of office',
    description: 'Automatic replies while you are away',
  },
  {
    id: 'notifications',
    to: '/settings/notifications',
    label: 'Notifications',
    description: 'Push, per-folder alerts, sender rules and quiet hours',
  },
  {
    id: 'privacy',
    to: '/settings/privacy',
    label: 'Privacy',
    description: 'Mail kept on this device, remote images and blocked senders',
  },
  {
    id: 'ai',
    to: '/settings/ai',
    label: 'AI assistant',
    description: 'Help writing and reviewing email, with your own Claude API key',
  },
  {
    id: 'nextcloud',
    to: '/settings/nextcloud',
    label: 'Nextcloud',
    description: 'Send large files as links from your own Nextcloud',
  },
  {
    id: 'sessions',
    to: '/settings/sessions',
    label: 'Sessions',
    description: 'Devices signed in to this mailbox, and how to sign them out',
  },
];

/**
 * The sections a deployment actually offers.
 *
 * Nextcloud is drawn only once `GET /nextcloud` has answered: a server without
 * the feature answers `501 unsupported_by_host`, and then the UI draws nothing
 * at all (ADR 0065's rule, plan 18 §5) -- not a disabled row, not a page that
 * says "not available". Until the answer is in, it is left out too, so the
 * row never appears and then vanishes.
 */
export function visibleSections(
  offers: { nextcloud: boolean },
  sections: readonly SettingsSectionLink[] = SETTINGS_SECTIONS,
): readonly SettingsSectionLink[] {
  return offers.nextcloud ? sections : sections.filter((section) => section.id !== 'nextcloud');
}

/**
 * General's address on handheld.
 *
 * On desktop `/settings` *is* General — the index route renders it beside the
 * nav. On a phone `/settings` is the section list, so General needs a URL of
 * its own: `/settings/general`, a real child route. (A `?section=general` param
 * on the index was tried first; a path is bookmarkable, reads as a place, and
 * does not make one route mean two screens.) On desktop that path renders
 * General too, so a link to it works at any width.
 */
export const GENERAL_ON_HANDHELD = '/settings/general';

/** Where a section's row on the handheld list goes. */
export function handheldPathOf(section: SettingsSectionLink): string {
  return section.id === 'general' ? GENERAL_ON_HANDHELD : section.to;
}

/**
 * The section on screen, or `undefined` for the handheld index.
 *
 * `/settings` without the param is the index; on desktop that is General, but
 * the caller decides that — this only reads the URL.
 */
export function sectionAt(pathname: string): SettingsSectionLink | undefined {
  const path = pathname.replace(/\/+$/, '');
  if (path === '/settings') return undefined;
  if (path === GENERAL_ON_HANDHELD) return SETTINGS_SECTIONS[0];
  return SETTINGS_SECTIONS.find((section) => section.to === path);
}

/** `history.state` from a row on the handheld list, so ← can be a real back. */
export const FROM_SETTINGS_INDEX = { from: 'settings-index' } as const;

/**
 * Router state that says "I came here to add an account".
 *
 * The account switcher's "Add account" is a link to this page, and the page
 * holds several cards. Landing on it and leaving the reader to find the right
 * one is most of the way to the dead button it replaced, so the add form
 * takes focus when this marker is present.
 */
export const ADD_ACCOUNT_FOCUS = 'add-account';

/**
 * Router state that says "I came here to re-enter this mailbox's password".
 *
 * A connected mailbox flagged `reauth-required` is never retried by the
 * server; the only way back is typing the password on its Accounts row. The
 * account switcher offers that under the mailbox, and this marker (with the
 * account id beside it) is how the row knows to open its form and take focus.
 */
export const REAUTH_ACCOUNT_FOCUS = 'reauth-account';
